<template>
	<section id="services" class="section section--dark">
		<div class="container">
			<header v-reveal class="section__head">
				<p class="section__eyebrow">{{ t('services.eyebrow') }}</p>
				<h2 class="section__title">{{ t('services.title') }}</h2>
				<p class="section__subtitle">{{ t('services.subtitle') }}</p>
			</header>

			<ol class="services__chain">
				<li
					v-for="(key, index) in SERVICE_KEYS"
					:key="key"
					v-reveal="index * 90"
					class="service"
				>
					<div class="service__icon">
						<img
							:src="serviceIconUrl(index)"
							alt=""
							loading="lazy"
						>
					</div>

					<div class="service__body">
						<span class="service__index">{{ paddedIndex(index) }}</span>
						<h3 class="service__title">{{ t(`services.items.${key}.title`) }}</h3>
						<p class="service__desc">{{ t(`services.items.${key}.desc`) }}</p>
					</div>
				</li>
			</ol>
		</div>
	</section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { SERVICE_KEYS } from '@/helpers/constants'
import { serviceIconUrl } from '@/helpers/functions'

defineOptions({ name: 'ServicesSection' })

const { t } = useI18n()

const paddedIndex = (index) => String(index + 1).padStart(2, '0')
</script>

<style scoped lang="scss">
.services__chain {
	position: relative;
	max-width: 820px;
	margin-inline: auto;
	display: flex;
	flex-direction: column;
	gap: clamp(6px, 1.4vw, 14px);
}

// the profile draws the services as a zig-zag chain: every other row mirrors
.service {
	display: flex;
	align-items: center;
	gap: clamp(16px, 2.6vw, 30px);
	padding: 14px 0;

	&:nth-child(even) {
		flex-direction: row-reverse;
		text-align: end;
	}

	@include rtl {
		flex-direction: row-reverse;
		text-align: end;

		&:nth-child(even) {
			flex-direction: row;
			text-align: start;
		}
	}

	@include below($bp-mobile) {
		flex-direction: column;
		text-align: center;
		gap: 14px;

		&:nth-child(even) {
			flex-direction: column;
			text-align: center;
		}
	}
}

.service__icon {
	position: relative;
	flex: none;
	width: clamp(84px, 11vw, 124px);
	height: clamp(84px, 11vw, 124px);

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		border-radius: 50%;
		filter: drop-shadow(0 8px 22px rgba(255, 208, 31, 0.28));
	}
}

.service__body {
	flex: 1;
	min-width: 0;
}

.service__index {
	display: block;
	margin-bottom: 6px;
	font-size: 0.74rem;
	font-weight: 700;
	letter-spacing: 0.2em;
	color: $color-gold;
}

.service__title {
	font-size: clamp(1.1rem, 2.4vw, 1.6rem);
	font-weight: 700;
	color: $color-paper;
}

.service__desc {
	margin-top: 8px;
	max-width: 44ch;
	font-size: 0.96rem;
	color: #a8a8a8;

	.service:nth-child(even) & {
		margin-inline-start: auto;
	}

	@include rtl {
		margin-inline-start: auto;

		.service:nth-child(even) & {
			margin-inline-start: 0;
			margin-inline-end: auto;
		}
	}

	@include below($bp-mobile) {
		margin-inline: auto;

		.service:nth-child(even) & {
			margin-inline: auto;
		}
	}
}
</style>
